import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS } from '../ruleExpectations.js'
import {
  buildEvaluationRecord,
  closeEvaluationDraft,
  emptyEvalDates,
  inferHiddenOpenEval,
  validateEvaluationRecord
} from '@/utils/studentEvaluation.js'

// DESTRUCTIVE: the path the Evaluation card uses — build + validate + write,
// date save, close as not-eligible. Asserts the student plan / caseManagerId
// / planStatus never change. Also waits for the Cloud Function mirror.
//
// Complements evalCaseManagerMirror (meeting clause + open-eval delete).
// This one is the card: close, then wait for the same CF to clear the mirror.
// inferHiddenOpenEval already treats a leftover own-uid mirror as lag.

const OTHER_CM = 'smoke-other-cm'
const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 75000

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function pollStudent(ref, check) {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let last = 'no read yet'
  while (Date.now() < deadline) {
    let state
    try {
      const snap = await getDoc(ref)
      state = { denied: false, exists: snap.exists(), data: snap.exists() ? snap.data() : null }
    } catch (err) {
      if (err?.code !== 'permission-denied') throw err
      state = { denied: true, exists: null, data: null }
    }
    const result = check(state)
    if (result) return result
    last = state.denied
      ? 'read denied'
      : `evalCaseManagerId=${JSON.stringify(state.data?.app?.evalCaseManagerId ?? null)}`
    await sleep(POLL_INTERVAL_MS)
  }
  throw new Error(
    `Timed out after ${POLL_TIMEOUT_MS / 1000}s waiting for Cloud Function (last: ${last}). ` +
      'Are functions deployed to this project?'
  )
}

function assertStudentUntouched(data, otherCm) {
  const plan = data?.app?.studentData?.plan
  const cm = data?.app?.studentData?.caseManagerId
  const planStatus = data?.app?.studentData?.planStatus || data?.app?.planStatus || ''
  if (plan !== '504') throw new Error(`SECURITY: student plan changed to ${plan}`)
  if (cm !== otherCm) throw new Error('SECURITY: student caseManagerId was reassigned')
  if (planStatus === 'initial') {
    throw new Error("SECURITY: wrote planStatus 'initial' on a student who has a plan")
  }
}

export default {
  id: 'evalCardLifecycle',
  label: 'Eval card create / dates / close (not eligible)',
  description:
    'Same writes the Evaluation card makes: create an IEP eval assigned to you on a 504 student, save a due date, close as not eligible. Student plan stays 504. Needs deployed functions.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const uid = ctx.user.uid

    if (!ctx.schoolId) {
      step.block('setup', 'Select a school before running the eval card smoke.')
      return
    }
    if (
      !RULE_EXPECTATIONS.studentCreate(role) ||
      !RULE_EXPECTATIONS.studentEvaluationCreate(role)
    ) {
      step.block(
        'setup',
        `Role ${role} cannot create students/evaluations — use a sped chair, psych, or admin (CMs work assigned evals, they do not start them).`
      )
      return
    }

    const studentRef = doc(collection(db, 'students'))
    const evalRef = doc(collection(db, 'studentEvaluations'))

    const studentDoc = {
      app: {
        studentData: {
          firstName: 'SMOKE',
          lastName: `EvalCard ${ctx.runId.slice(-8)}`,
          plan: '504',
          grade: '8',
          ssid: `SMOKE-EVALCARD-${ctx.runId}`,
          caseManagerId: OTHER_CM
        },
        accommodations: { instruction: '', assessment: '' },
        schedule: { classServices: [] },
        staffIds: []
      },
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      schoolId: ctx.schoolId
    }

    try {
      await step('create 504 student (plan CM is someone else)', async () => {
        await setDoc(studentRef, studentDoc)
        return `students/${studentRef.id}`
      })

      await step('card create: build + validate + write eval assigned to you', async () => {
        // Consent at create: this scenario tests the post-consent card
        // lifecycle. The referral-window (no mirror before consent) is
        // covered by evalCaseManagerMirror / evalFillableShell.
        const record = buildEvaluationRecord({
          studentId: studentRef.id,
          schoolId: ctx.schoolId,
          evalType: 'IEP',
          caseManagerId: uid,
          createdBy: uid,
          dates: { ...emptyEvalDates(), referral: '2026-08-01', consent: '2026-08-10' },
          source: 'manual'
        })
        if (Object.prototype.hasOwnProperty.call(record, 'status')) {
          throw new Error('Card payload must not include a status field')
        }
        const check = validateEvaluationRecord(record, { existing: [] })
        if (!check.ok)
          throw new Error(check.errors[0]?.message || 'validateEvaluationRecord failed')
        await setDoc(evalRef, {
          ...record,
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return `studentEvaluations/${evalRef.id}`
      })

      await step('Cloud Function mirrors eval CM onto the student', async () => {
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied || !data) return null
          assertStudentUntouched(data, OTHER_CM)
          const mirror = data.app?.evalCaseManagerId || ''
          const staffIds = data.app?.staffIds || []
          if (mirror === uid && staffIds.includes(uid)) {
            return 'evalCaseManagerId + staffIds set; plan still 504'
          }
          return null
        })
      })

      await step('card save dates (hand-entered due — not computed)', async () => {
        // Keep consent: dropping it would re-hide the mirror mid-scenario.
        await updateDoc(evalRef, {
          dates: {
            ...emptyEvalDates(),
            referral: '2026-08-01',
            consent: '2026-08-10',
            due: '2026-09-15'
          },
          updatedAt: serverTimestamp()
        })
        const snap = await getDoc(evalRef)
        const dates = snap.data()?.dates || {}
        if (dates.due !== '2026-09-15') throw new Error('Due date did not save')
        if (snap.data()?.status) throw new Error('Must not store a status field')
        return 'due date 2026-09-15 saved'
      })

      await step('card close as not eligible — student unchanged', async () => {
        const current = (await getDoc(evalRef)).data()
        const result = closeEvaluationDraft(current, {
          outcome: 'notEligible',
          closedDate: '2026-08-29'
        })
        if (!result.ok) throw new Error(result.errors[0]?.message || 'closeEvaluationDraft failed')
        await updateDoc(evalRef, { ...result.patch, updatedAt: serverTimestamp() })
        return 'closed notEligible'
      })

      await step(
        'leftover evalCaseManagerId after close is CF lag (inferHiddenOpenEval stays false)',
        async () => {
          const snap = await getDoc(studentRef)
          const leftover = snap.exists() ? snap.data()?.app?.evalCaseManagerId || '' : ''
          const hidden = inferHiddenOpenEval({
            visibleOpenEval: null,
            mirrorEvalCaseManagerId: leftover,
            currentUid: uid,
            justClosed: true
          })
          if (hidden) {
            throw new Error(
              'leftover own-uid mirror after close must not look like a hidden open eval'
            )
          }
          return leftover === uid
            ? 'mirror still set; inferHiddenOpenEval is false (CF lag)'
            : leftover === ''
              ? 'mirror already cleared; inferHiddenOpenEval is false'
              : `mirror=${leftover}; inferHiddenOpenEval is false`
        }
      )

      await step('mirror clears; plan still 504', async () => {
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied) return 'read denied — uid left staffIds; plan untouched for this role'
          if (!data) return 'student already gone'
          assertStudentUntouched(data, OTHER_CM)
          const mirror = data.app?.evalCaseManagerId || ''
          const staffIds = data.app?.staffIds || []
          if (mirror === '' && !staffIds.includes(uid)) {
            return 'mirror cleared; caseManagerId still the 504 CM'
          }
          return null
        })
      })
    } finally {
      await deleteDoc(evalRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
